/**
 * Infinite scroll component
 */
import isEqual from 'lodash.isequal';
import React, { createRef, forwardRef, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getBodyScrollHeight, getBodyScrollPositionY } from 'utils/dom-element-helpers';

export type InfiniteScrollProps = {
  loadMore: (
    page: number,
    params: {
      limit: number;
      offset: number;
    },
  ) => void | Promise<unknown>;
  dataLength: number;
  batchSize: number;
  children: React.ReactNode;
  hasMore: boolean;
  loader?: React.ReactNode;
  threshold?: number;
  parentRef?: RefObject<any>;
  startPage?: number;
  manualLoadFirstSet?: boolean;
  resetDependencies?: Array<any>; // Reset happens when these dependencies change
  disabled?: boolean;
  beforeEachLoad?: (args: InfiniteScrollRefType) => boolean | void; // Return true to stop loading next page.
};

export type InfiniteScrollRefType = {
  reset: () => void;
};

const InfiniteScroll = forwardRef((props: InfiniteScrollProps, ref: RefObject<InfiniteScrollRefType>) => {
  const {
    children,
    dataLength,
    batchSize,
    loadMore,
    hasMore,
    loader,
    threshold = 250,
    parentRef,
    manualLoadFirstSet,
    resetDependencies,
    disabled,
    beforeEachLoad,
  } = props;

  // Calculate current state based on data length
  const totalPages = Math.ceil(dataLength / batchSize);
  const fullPagesCount = Math.floor(dataLength / batchSize);

  // Check if we have a partial final page (important for cases like 3.5 pages of data)
  const hasPartialFinalPage = dataLength % batchSize !== 0 && dataLength > 0;

  // Current loaded page is the last full page, or 0 if we only have partial data
  const currentLoadedPage = fullPagesCount > 0 ? fullPagesCount - 1 : 0;

  // Next page to load
  const nextPage = totalPages;

  // State and refs
  const [previousDeps, setPreviousDeps] = useState(resetDependencies);
  const [pageToLoad, setPageToLoad] = useState(nextPage);
  const lastLoadedPageRef = useRef(dataLength > 0 ? currentLoadedPage : -1);
  const firstMountRef = useRef(true);
  // The load in flight, if any. The next page waits for it: callers build the next list from the
  // list they saw when the request started, so two overlapping loads overwrite each other.
  const pendingLoadRef = useRef<object | null>(null);
  const [, setLoadsSettled] = useState(0);
  const wrapperRef = createRef<HTMLDivElement>();

  // Reset scroll state
  const reset = () => {
    pendingLoadRef.current = null;
    setPreviousDeps(resetDependencies);
    lastLoadedPageRef.current = -1;
    setPageToLoad(0);
  };

  // Expose reset function
  useImperativeHandle(ref, (): InfiniteScrollRefType => ({ reset }));

  // Update page when data changes
  useEffect(() => {
    // If this is initial mount with existing data,
    // skip loading to avoid duplicate requests when returning to page
    if (firstMountRef.current && dataLength > 0) {
      firstMountRef.current = false;

      // Mark appropriate pages as loaded based on existing data
      lastLoadedPageRef.current = currentLoadedPage;

      // If we have a partial final page and we've loaded all full pages,
      // mark the partial page as loaded too to prevent further loading
      if (hasPartialFinalPage && pageToLoad === totalPages - 1) {
        lastLoadedPageRef.current = totalPages - 1;
      }

      return;
    }

    firstMountRef.current = false;

    // For data with only a single partial page, assume we're done loading.
    // Only apply this optimization when there is no more data to load.
    if (!hasMore && dataLength > 0 && dataLength < batchSize) {
      lastLoadedPageRef.current = 0;

      return;
    }

    // Normal case: update next page to load. Never derive backwards past a page we already
    // requested — providers that filter rows server-side (e.g. Advec dropping sold ids) return
    // short pages, so the item-count-derived nextPage can lag behind the requests actually made.
    setPageToLoad(Math.max(nextPage, lastLoadedPageRef.current + 1));
  }, [dataLength, batchSize, currentLoadedPage, hasPartialFinalPage, nextPage, pageToLoad, totalPages, hasMore]);

  // Core loading function
  const load = () => {
    if (disabled) {
      return;
    }
    if (manualLoadFirstSet && pageToLoad === 0) {
      return;
    }
    if (pendingLoadRef.current) {
      return;
    }

    // Never reload page 0 if we have data
    if (pageToLoad === 0 && dataLength > 0) {
      lastLoadedPageRef.current = 0;

      return;
    }

    // For initial data that's less than a page size, assume we have all data
    // Only apply this optimization when there is no more data to load.
    if (!hasMore && dataLength > 0 && dataLength < batchSize) {
      lastLoadedPageRef.current = 0;

      return;
    }

    // If we've loaded all full pages and have a partial final page, don't try to load more
    // Only apply this optimization when there is no more data to load.
    if (!hasMore && hasPartialFinalPage && pageToLoad >= totalPages - 1) {
      lastLoadedPageRef.current = totalPages - 1;

      return;
    }

    // Check if we should load more based on scroll position
    const parentElement = parentRef?.current;
    const scrollPosition = parentElement
      ? parentElement.scrollTop + parentElement.offsetHeight
      : getBodyScrollPositionY() + window.innerHeight;
    const scrollHeight = parentElement ? parentElement.scrollHeight : getBodyScrollHeight();

    const isScrolledNearBottom = scrollHeight - scrollPosition < threshold;
    const isPageAlreadyLoaded = pageToLoad <= lastLoadedPageRef.current;
    const isParentHidden = wrapperRef?.current?.parentElement?.offsetHeight === 0;

    // Only load if all conditions are met
    if (hasMore && isScrolledNearBottom && !isPageAlreadyLoaded && !isParentHidden) {
      lastLoadedPageRef.current = pageToLoad;

      const pendingLoad = {};

      pendingLoadRef.current = pendingLoad;

      Promise.resolve(
        loadMore(pageToLoad, {
          limit: batchSize,
          offset: pageToLoad * batchSize,
        }),
      ).finally(() => {
        if (pendingLoadRef.current === pendingLoad) {
          pendingLoadRef.current = null;
          // Re-render so the next page is checked with the caller's updated list.
          setLoadsSettled((count) => count + 1);
        }
      });
    }
  };

  // Setup scroll listeners and handle reset
  useEffect(() => {
    if (disabled) {
      return;
    }

    // Handle custom loading control
    if (beforeEachLoad?.({ reset }) === true) {
      return;
    }

    // Reset when dependencies change
    if (!isEqual(resetDependencies, previousDeps)) {
      reset();

      return;
    }

    const parentElement = parentRef?.current;
    const target = parentElement || window;

    // Add scroll/resize listeners
    target.addEventListener('scroll', load);
    target.addEventListener('resize', load);

    // Initial check for loading
    load();

    // Cleanup listeners
    return () => {
      target.removeEventListener('scroll', load);
      target.removeEventListener('resize', load);
    };
  });

  // Determine if we should show the loader
  const isCompleteData = hasPartialFinalPage || (dataLength > 0 && dataLength < batchSize);

  return (
    <>
      {/* Wrapper element for detecting parent visibility */}
      <div ref={wrapperRef} style={{ display: 'none' }} />

      {/* Content */}
      {children}

      {/* Loader - hide when we have complete data (all available data is loaded) */}
      {hasMore && !isCompleteData && loader}
    </>
  );
});

export default InfiniteScroll;
